import { useState, useEffect, useMemo } from "react";
import { Briefcase, Edit, Trash2, Plus, ArrowLeft, GripVertical, Tag, ListChecks, Search, Check } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api from "../api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SortableCategoryRow = ({ category, t, serviceCount, onAssign, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}>
      <div className={`flex items-center gap-3 p-3 rounded-xl backdrop-blur-md bg-white/50 border border-white/30 shadow-sm transition-all ${isDragging ? 'opacity-80 ring-2 ring-blue-500 shadow-lg' : ''}`}>
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="p-1.5 -ml-1 rounded-lg text-zinc-400 hover:text-zinc-600 active:bg-white/50 touch-none transition-colors"
          aria-label={t('services.management.dragToReorder', 'Sürükleyerek sırala')}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <Tag className="w-4 h-4 text-indigo-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-zinc-900 break-words leading-snug">{category.name}</span>
          <span className="block text-xs text-zinc-500 font-medium mt-0.5">
            {serviceCount > 0
              ? t('services.categories.serviceCount', { count: serviceCount })
              : t('services.categories.noServices')}
          </span>
        </div>
        <Button onClick={() => onAssign(category)} size="sm" variant="outline" className="backdrop-blur-md bg-white/60 border-white/40 hover:bg-white/80 rounded-lg font-bold shadow-sm h-8 px-2.5">
          <ListChecks className="w-3.5 h-3.5 sm:mr-1" />
          <span className="hidden sm:inline text-xs">{t('services.categories.assign')}</span>
        </Button>
        <Button onClick={() => onEdit(category)} size="sm" variant="outline" className="backdrop-blur-md bg-white/60 border-white/40 hover:bg-white/80 rounded-lg font-bold shadow-sm h-8 px-2">
          <Edit className="w-3.5 h-3.5" />
        </Button>
        <Button onClick={() => onDelete(category)} size="sm" variant="outline" className="text-red-600 hover:bg-red-50 backdrop-blur-md bg-white/60 border-white/40 hover:border-red-300 rounded-lg font-bold shadow-sm h-8 px-2">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

const SortableServiceCard = ({ service, categoryName, i18n, t, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: service.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        data-testid={`service-card-${service.id}`}
        className={`backdrop-blur-xl bg-white/40 border border-white/20 rounded-2xl p-6 shadow-lg transition-[box-shadow,background-color,opacity,transform] duration-200 ${
          isDragging ? 'opacity-80 ring-2 ring-blue-500 shadow-2xl scale-105' : 'hover:shadow-xl hover:bg-white/50'
        }`}
      >
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              type="button"
              {...attributes}
              {...listeners}
              className="p-2 -ml-2 rounded-xl text-zinc-400 hover:text-zinc-600 active:bg-white/50 touch-none transition-colors"
              aria-label={t('services.management.dragToReorder', 'Sürükleyerek sırala')}
            >
              <GripVertical className="w-5 h-5" />
            </button>

            <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
              <Briefcase className="w-5 h-5 text-white" />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-base font-black text-zinc-900 mb-1 break-words leading-tight">{service.name}</h3>
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-lg font-black text-blue-600">{Math.round(service.price)}{i18n.language === 'tr' ? '₺' : '£'}</p>
                <p className="text-sm text-zinc-600 font-bold">{(service.duration || 30)} {t('services.management.minutes')}</p>
                {service.session_count && service.session_count > 1 && (
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-bold">
                    {service.session_count} {i18n.language === 'tr' ? 'seans' : 'sessions'}
                  </span>
                )}
                {categoryName && (
                  <span className="text-xs bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1">
                    <Tag className="w-3 h-3" />{categoryName}
                  </span>
                )}
                {service.payment_rule && service.payment_rule !== 'on_site' && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    service.payment_rule === 'online' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {service.payment_rule === 'online' 
                      ? (i18n.language === 'tr' ? 'Online Ödeme' : 'Online Payment')
                      : (i18n.language === 'tr' 
                          ? `Kapora ${service.deposit_amount ? Math.round(service.deposit_amount) + '₺' : '%' + (service.deposit_percentage || 30)}`
                          : `Deposit ${service.deposit_amount ? '£' + Math.round(service.deposit_amount) : (service.deposit_percentage || 30) + '%'}`)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2 flex-shrink-0 self-start">
            <Button
              data-testid={`edit-service-${service.id}`}
              onClick={() => onEdit(service)}
              size="sm"
              variant="outline"
              className="backdrop-blur-md bg-white/60 border-white/40 hover:bg-white/80 rounded-xl font-bold shadow-sm"
            >
              <Edit className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">{t('common.edit')}</span>
            </Button>
            <Button
              data-testid={`delete-service-${service.id}`}
              onClick={() => onDelete(service)}
              size="sm"
              variant="outline"
              className="text-red-600 hover:bg-red-50 backdrop-blur-md bg-white/60 border-white/40 hover:border-red-300 rounded-xl font-bold shadow-sm"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServiceManagement = ({ services, onRefresh, onNavigate }) => {
  const { t, i18n } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [showAssignReminder, setShowAssignReminder] = useState(false);
  const [lastAddedServiceName, setLastAddedServiceName] = useState("");
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({ name: "", price: "", duration: "30", isSessionPackage: false, sessionCount: "2", paymentRule: "on_site", depositAmount: "", categoryId: "" });
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  // Minimum online ödeme / kapora eşikleri para birimine göre değişir.
  // Kaynak: backend financial/money.py (MIN_ONLINE_PAYMENT_MINOR / MIN_DEPOSIT_MINOR).
  const paymentMinimums = useMemo(() => {
    const phone = (settings?.support_phone || '').replace(/\s/g, '');
    const isGbp = phone ? (phone.startsWith('+44') || phone.startsWith('44')) : i18n.language !== 'tr';
    return isGbp ? { online: 5, deposit: 3 } : { online: 300, deposit: 200 };
  }, [settings, i18n.language]);
  const [orderedServices, setOrderedServices] = useState([]);
  const [reordering, setReordering] = useState(false);
  // Kategoriler
  const [categories, setCategories] = useState([]);
  const [orderedCategories, setOrderedCategories] = useState([]);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState(null);
  const [savingCategory, setSavingCategory] = useState(false);
  // Kategoriye toplu hizmet atama
  const [assignCategory, setAssignCategory] = useState(null);
  const [assignSelection, setAssignSelection] = useState(new Set());
  const [assignSearch, setAssignSearch] = useState("");
  const [savingAssignment, setSavingAssignment] = useState(false);

  useEffect(() => {
    setOrderedServices(Array.isArray(services) ? services : []);
  }, [services]);

  useEffect(() => {
    setOrderedCategories(Array.isArray(categories) ? categories : []);
  }, [categories]);

  const serviceIds = useMemo(() => orderedServices.map((s) => s.id), [orderedServices]);
  const categoryIds = useMemo(() => orderedCategories.map((c) => c.id), [orderedCategories]);
  const categoryNameById = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [categories]);

  const serviceCountByCategory = useMemo(() => {
    const m = {};
    (services || []).forEach((s) => {
      if (s?.category_id) m[s.category_id] = (m[s.category_id] || 0) + 1;
    });
    return m;
  }, [services]);

  const loadCategories = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Kategoriler yüklenemedi:", e);
    }
  };

  const openAssignDialog = (category) => {
    const current = (services || []).filter((s) => s?.category_id === category.id).map((s) => s.id);
    setAssignSelection(new Set(current));
    setAssignSearch("");
    setAssignCategory(category);
  };

  const toggleAssignService = (serviceId) => {
    setAssignSelection((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) next.delete(serviceId);
      else next.add(serviceId);
      return next;
    });
  };

  const assignableServices = useMemo(() => {
    const term = assignSearch.trim().toLocaleLowerCase('tr');
    const list = services || [];
    if (!term) return list;
    return list.filter((s) => String(s?.name || "").toLocaleLowerCase('tr').includes(term));
  }, [services, assignSearch]);

  const handleSaveAssignment = async () => {
    if (!assignCategory) return;
    setSavingAssignment(true);
    try {
      const res = await api.post(`/categories/${assignCategory.id}/services`, {
        service_ids: Array.from(assignSelection),
      });
      const { assigned_count = 0, removed_count = 0 } = res.data || {};
      toast.success(t('services.categories.assignSaved', {
        category: assignCategory.name,
        added: assigned_count,
        removed: removed_count,
      }));
      setAssignCategory(null);
      onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || t('services.categories.assignFailed', 'Atama kaydedilemedi'));
    } finally {
      setSavingAssignment(false);
    }
  };

  useEffect(() => { loadCategories(); }, []);

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) return;
    setSavingCategory(true);
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, { name: categoryName.trim() });
        toast.success(t('services.categories.updated', 'Kategori güncellendi'));
      } else {
        await api.post('/categories', { name: categoryName.trim() });
        toast.success(t('services.categories.added', 'Kategori eklendi'));
      }
      setCategoryDialog(false);
      setCategoryName("");
      setEditingCategory(null);
      await loadCategories();
    } catch (error) {
      toast.error(t('services.categories.saveFailed', 'Kategori kaydedilemedi'));
    } finally {
      setSavingCategory(false);
    }
  };

  const handleCategoryDelete = async (categoryId) => {
    try {
      await api.delete(`/categories/${categoryId}`);
      toast.success(t('services.categories.deleted', 'Kategori silindi'));
      setDeleteCategoryDialog(null);
      await loadCategories();
      onRefresh();
    } catch (error) {
      toast.error(t('services.categories.deleteFailed', 'Kategori silinemedi'));
    }
  };

  const persistCategoryOrder = async (next) => {
    try {
      await api.post('/categories/reorder', { category_ids: next.map((c) => c.id) });
      await loadCategories();
    } catch (error) {
      await loadCategories();
    }
  };

  const handleCategoryDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrderedCategories((prev) => {
      const oldIndex = prev.findIndex((c) => c.id === active.id);
      const newIndex = prev.findIndex((c) => c.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistCategoryOrder(next);
      return next;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({ 
      name: service.name, 
      price: service.price.toString(),
      duration: (service.duration || 30).toString(),
      isSessionPackage: !!(service.session_count && service.session_count > 1),
      sessionCount: (service.session_count && service.session_count > 1) ? service.session_count.toString() : "2",
      paymentRule: service.payment_rule || "on_site",
      depositAmount: service.deposit_amount ? service.deposit_amount.toString() : "",
      categoryId: service.category_id || ""
    });
    setFormErrors({});
    setShowDialog(true);
  };

  const persistOrder = async (nextServices) => {
    setReordering(true);
    try {
      await api.post('/services/reorder', { service_ids: nextServices.map(s => s.id) });
      toast.success(t('services.management.reordered', 'Sıralama güncellendi'));
      onRefresh();
    } catch (error) {
      toast.error(t('services.management.reorderFailed', 'Sıralama güncellenemedi'));
      onRefresh();
    } finally {
      setReordering(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedServices((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistOrder(next);
      return next;
    });
  };

  const handleNew = () => {
    setEditingService(null);
    setFormData({ name: "", price: "", duration: "30", isSessionPackage: false, sessionCount: "2", paymentRule: "on_site", depositAmount: "", categoryId: "" });
    setFormErrors({});
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    
    if (!formData.name || !formData.price || !formData.duration) {
      if (!formData.name) errors.name = i18n.language === 'tr' ? 'Hizmet adı gereklidir.' : 'Service name is required.';
      if (!formData.price) errors.price = i18n.language === 'tr' ? 'Fiyat gereklidir.' : 'Price is required.';
      if (!formData.duration) errors.duration = i18n.language === 'tr' ? 'Süre gereklidir.' : 'Duration is required.';
      setFormErrors(errors);
      return;
    }

    const price = parseFloat(formData.price);
    if (isNaN(price) || price <= 0) {
      setFormErrors({ price: i18n.language === 'tr' ? 'Geçerli bir fiyat girin.' : 'Enter a valid price.' });
      return;
    }

    // Uyarı ödeme kuralının altında gösteriliyor; fiyat alanının altında kalırsa
    // form kaydırıldığında görünmüyordu.
    if (formData.paymentRule === 'online' && price < paymentMinimums.online) {
      setFormErrors({ paymentRule: i18n.language === 'tr' ? `Online ödeme için hizmet fiyatı en az ${paymentMinimums.online}₺ olmalıdır.` : `Online payment requires a service price of at least £${paymentMinimums.online}.` });
      return;
    }
    if (formData.paymentRule === 'deposit' && price < paymentMinimums.online) {
      setFormErrors({ paymentRule: i18n.language === 'tr' ? `Kapora için hizmet fiyatı en az ${paymentMinimums.online}₺ olmalıdır.` : `Taking a deposit requires a service price of at least £${paymentMinimums.online}.` });
      return;
    }
    if (formData.paymentRule === 'deposit') {
      const dep = parseFloat(formData.depositAmount) || 0;
      if (dep < paymentMinimums.deposit) {
        setFormErrors({ depositAmount: i18n.language === 'tr' ? `Kapora tutarı en az ${paymentMinimums.deposit}₺ olmalıdır.` : `The deposit must be at least £${paymentMinimums.deposit}.` });
        return;
      }
      if (dep > price) {
        setFormErrors({ depositAmount: i18n.language === 'tr' ? 'Kapora tutarı, hizmetin toplam fiyatını aşamaz.' : 'Deposit cannot exceed the service price.' });
        return;
      }
    }

    const duration = parseInt(formData.duration);
    if (isNaN(duration) || duration <= 0) {
      setFormErrors({ duration: i18n.language === 'tr' ? 'Geçerli bir süre girin.' : 'Enter a valid duration.' });
      return;
    }
    setFormErrors({});

    setLoading(true);
    try {
      const payload = { 
        name: formData.name, price, duration, 
        session_count: formData.isSessionPackage ? parseInt(formData.sessionCount) : null,
        payment_rule: formData.paymentRule || "on_site",
        deposit_amount: formData.paymentRule === "deposit" ? parseFloat(formData.depositAmount) || null : null,
        category_id: formData.categoryId || null
      };
      
      if (editingService) {
        await api.put(`/services/${editingService.id}`, payload);
        toast.success(t('services.management.serviceUpdated'));
      } else {
        await api.post("/services", payload);
        toast.success(t('services.management.serviceAdded'));
        // Yeni hizmet artık backend'de tüm aktif admin+staff'a otomatik atanıyor.
        // Personel Yönetimi hatırlatma dialog'una artık gerek yok; gerekirse aşağıdaki satırları aç.
        // setLastAddedServiceName(formData.name);
        // setShowAssignReminder(true);
      }
      
      setShowDialog(false);
      setFormData({ name: "", price: "", duration: "30", isSessionPackage: false, sessionCount: "2", paymentRule: "on_site", depositAmount: "", categoryId: "" });
      onRefresh();
    } catch (error) {
      toast.error(t('services.management.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serviceId) => {
    try {
      await api.delete(`/services/${serviceId}`);
      toast.success(t('services.management.serviceDeleted'));
      setDeleteDialog(null);
      onRefresh();
    } catch (error) {
      toast.error(t('services.management.deleteFailed'));
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get("/settings");
      setSettings(response.data);
    } catch (error) {
      console.error("Ayarlar yüklenemedi:", error);
    }
  };

  const handleSettingsChange = async (field, value) => {
    if (!settings) return;
    
    setSavingSettings(true);
    try {
      const updatedSettings = { ...settings, [field]: value };
      await api.put("/settings", updatedSettings);
      setSettings(updatedSettings);
      toast.success(t('services.management.settingUpdated'));
    } catch (error) {
      toast.error(t('services.management.settingUpdateFailed'));
      console.error("Settings update error:", error);
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 pb-20">
      {/* KART 1: Başlık ve Yeni Hizmet Ekle */}
      <div className="px-4 pt-6 pb-4">
        <div className="backdrop-blur-xl bg-white/40 border border-white/20 rounded-2xl p-6 shadow-lg">
          <div className="space-y-4">
            <div className="mb-4">
              <button
                onClick={() => onNavigate && onNavigate("settings")}
                className="flex items-center gap-2 text-zinc-700 hover:text-zinc-900 mb-4 transition-colors p-2 -ml-2 hover:bg-white/50 rounded-xl"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-bold">{t('settings.backToSettings')}</span>
              </button>
              <div>
                <h2 className="text-xl font-black text-zinc-900">{t('services.management.title')}</h2>
                <p className="text-sm text-zinc-600 mt-1 font-medium">{t('services.management.subtitle')}</p>
              </div>
            </div>

            <Button
              data-testid="add-service-button"
              onClick={handleNew}
              className="w-full bg-zinc-900 hover:bg-black h-12 text-base font-bold rounded-xl shadow-lg transition-all"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('services.management.newService')}
            </Button>
          </div>
        </div>
      </div>

      {/* KART: Kategoriler */}
      <div className="px-4 pt-4 pb-4">
        <div className="backdrop-blur-xl bg-white/40 border border-white/20 rounded-2xl p-6 shadow-lg">
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-black text-zinc-900 mb-1">{t('services.categories.title', 'Kategoriler')}</h3>
                <p className="text-sm text-zinc-600 font-medium">{t('services.categories.subtitle', 'Hizmetlerinizi gruplayın ve müşteri sayfasındaki sırayı belirleyin')}</p>
              </div>
              <Button
                onClick={() => { setEditingCategory(null); setCategoryName(""); setCategoryDialog(true); }}
                size="sm"
                className="bg-zinc-900 hover:bg-black rounded-xl font-bold shadow-md flex-shrink-0"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('services.categories.add', 'Kategori')}
              </Button>
            </div>

            {orderedCategories.length === 0 ? (
              <p className="text-sm text-zinc-500 font-medium py-2">{t('services.categories.empty', 'Henüz kategori yok. İsteğe bağlıdır.')}</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
                <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {orderedCategories.map((category) => (
                      <SortableCategoryRow
                        key={category.id}
                        category={category}
                        t={t}
                        serviceCount={serviceCountByCategory[category.id] || 0}
                        onAssign={openAssignDialog}
                        onEdit={(c) => { setEditingCategory(c); setCategoryName(c.name); setCategoryDialog(true); }}
                        onDelete={setDeleteCategoryDialog}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>

      {/* KART: Online Randevu Ayarları */}
      <div className="px-4 pt-4 pb-4">
        <div className="backdrop-blur-xl bg-white/40 border border-white/20 rounded-2xl p-6 shadow-lg">
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-black text-zinc-900 mb-1">{t('services.management.onlineSettings')}</h3>
              <p className="text-sm text-zinc-600 font-medium">{t('services.management.onlineSettingsDescription')}</p>
            </div>
            
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between p-4 rounded-xl backdrop-blur-md bg-white/50 border border-white/30 shadow-sm">
                <div className="flex-1">
                  <Label htmlFor="show-duration" className="text-sm font-bold text-zinc-900 cursor-pointer">
                    {t('services.management.showDuration')}
                  </Label>
                  <p className="text-xs text-zinc-600 mt-1 font-medium">{t('services.management.showDurationNote')}</p>
                </div>
                <Switch
                  id="show-duration"
                  checked={settings?.show_service_duration_on_public !== false}
                  onCheckedChange={(checked) => handleSettingsChange("show_service_duration_on_public", checked)}
                  disabled={savingSettings || !settings}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl backdrop-blur-md bg-white/50 border border-white/30 shadow-sm">
                <div className="flex-1">
                  <Label htmlFor="show-price" className="text-sm font-bold text-zinc-900 cursor-pointer">
                    {t('services.management.showPrice')}
                  </Label>
                  <p className="text-xs text-zinc-600 mt-1 font-medium">{t('services.management.showPriceNote')}</p>
                </div>
                <Switch
                  id="show-price"
                  checked={settings?.show_service_price_on_public !== false}
                  onCheckedChange={(checked) => handleSettingsChange("show_service_price_on_public", checked)}
                  disabled={savingSettings || !settings}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl backdrop-blur-md bg-white/50 border border-white/30 shadow-sm">
                <div className="flex-1">
                  <Label htmlFor="multi-service" className="text-sm font-bold text-zinc-900 cursor-pointer">
                    {t('services.management.multiService', 'Çoklu Hizmet Seçimi')}
                  </Label>
                  <p className="text-xs text-zinc-600 mt-1 font-medium">{t('services.management.multiServiceNote', 'Müşteriler tek randevuda birden fazla hizmet seçebilir')}</p>
                </div>
                <Switch
                  id="multi-service"
                  checked={settings?.multi_service_enabled === true}
                  onCheckedChange={(checked) => handleSettingsChange("multi_service_enabled", checked)}
                  disabled={savingSettings || !settings}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KART 2: Hizmet Listesi */}
      {services.length === 0 ? (
        <div className="px-4 py-4">
          <div className="backdrop-blur-xl bg-white/40 border border-white/20 rounded-2xl p-6 shadow-lg">
            <div className="text-center py-8">
              <Briefcase className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <h3 className="text-base font-black text-zinc-900 mb-2">{t('services.management.noServicesTitle')}</h3>
              <p className="text-sm text-zinc-600 font-medium">{t('services.management.noServicesDescription')}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 py-4 space-y-3">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={serviceIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {orderedServices.map((service) => (
                  <SortableServiceCard
                    key={service.id}
                    service={service}
                    categoryName={service.category_id ? categoryNameById[service.category_id] : null}
                    i18n={i18n}
                    t={t}
                    onEdit={handleEdit}
                    onDelete={setDeleteDialog}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {reordering && (
            <div className="text-center text-xs text-zinc-500 pt-2 font-bold animate-pulse">
              {t('common.saving', 'Kaydediliyor...')}
            </div>
          )}
        </div>
      )}

      {/* Service Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md backdrop-blur-2xl bg-white/95 border-white/30 rounded-3xl shadow-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0 pb-2">
            <DialogTitle className="text-xl font-black text-zinc-900">
              {editingService ? t('services.management.editTitle') : t('services.management.addTitle')}
            </DialogTitle>
            <DialogDescription className="text-zinc-600 font-medium">
              {t('services.management.dialogDescription')}
            </DialogDescription>
          </DialogHeader>
          {/* `pr-2` scroll bar için sağ tarafta nefes payı bırakır; `px-1` ile sol+sağ simetri.
              `overflow-x-hidden` mobilde 1px taşmaları engeller. */}
          <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto overflow-x-hidden flex-1 px-1 pr-2">
            {/* Section: Temel Bilgiler */}
            <div className="flex items-center gap-3">
              <h3 className="text-[11px] font-black uppercase tracking-[0.12em] text-zinc-400">
                {i18n.language === 'tr' ? 'Temel Bilgiler' : 'Basic Info'}
              </h3>
              <div className="flex-1 h-px bg-zinc-200/70" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-name" className="text-sm font-bold text-zinc-700">{t('services.fields.name')}</Label>
              <Input
                id="service-name"
                data-testid="service-name-input"
                value={formData.name}
                onChange={(e) => { setFormErrors(prev => { const { name: _, ...rest } = prev; return rest; }); setFormData({ ...formData, name: e.target.value }); }}
                placeholder={t('services.management.pricePlaceholder')}
                required
                className={`backdrop-blur-md bg-white/60 border rounded-xl h-11 focus:ring-2 focus:ring-zinc-900 font-medium ${formErrors.name ? 'border-red-400' : 'border-white/40'}`}
              />
              {formErrors.name && <p className="text-xs text-red-500 font-medium">{formErrors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-price" className="text-sm font-bold text-zinc-700">
                {t('services.fields.price')} ({i18n.language === 'tr' ? '₺' : '£'})
              </Label>
              <Input
                id="service-price"
                data-testid="service-price-input"
                type="number"
                step="1"
                value={formData.price}
                onChange={(e) => { setFormErrors(prev => { const { price: _, ...rest } = prev; return rest; }); setFormData({ ...formData, price: e.target.value }); }}
                placeholder="0"
                required
                className={`backdrop-blur-md bg-white/60 border rounded-xl h-11 focus:ring-2 focus:ring-zinc-900 font-medium ${formErrors.price ? 'border-red-400' : 'border-white/40'}`}
              />
              {formErrors.price && <p className="text-xs text-red-500 font-medium">{formErrors.price}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-duration" className="text-sm font-bold text-zinc-700">{t('services.fields.duration')}</Label>
              <Input
                id="service-duration"
                data-testid="service-duration-input"
                type="number"
                min="1"
                step="1"
                value={formData.duration}
                onChange={(e) => { setFormErrors(prev => { const { duration: _, ...rest } = prev; return rest; }); setFormData({ ...formData, duration: e.target.value }); }}
                placeholder="30"
                required
                className={`backdrop-blur-md bg-white/60 border rounded-xl h-11 focus:ring-2 focus:ring-zinc-900 font-medium ${formErrors.duration ? 'border-red-400' : 'border-white/40'}`}
              />
              {formErrors.duration 
                ? <p className="text-xs text-red-500 font-medium">{formErrors.duration}</p>
                : <p className="text-xs text-zinc-500 font-medium">{t('services.management.durationNote')}</p>
              }
            </div>
            {/* Kategori seçimi formdan kaldırıldı; atama kategori kartından yapılacak.
                `formData.categoryId` yine de taşınıyor — düzenlemede gönderilmezse
                backend `category_id`'yi unset ediyor ve mevcut atama siliniyor. */}
            {/* Section: Paket Tipi */}
            <div className="flex items-center gap-3 pt-2">
              <h3 className="text-[11px] font-black uppercase tracking-[0.12em] text-zinc-400">
                {i18n.language === 'tr' ? 'Paket Tipi' : 'Package Type'}
              </h3>
              <div className="flex-1 h-px bg-zinc-200/70" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-xl backdrop-blur-md bg-white/50 border border-white/30 shadow-sm">
              <div className="flex-1">
                <Label htmlFor="session-toggle" className="text-sm font-bold text-zinc-900 cursor-pointer">
                  {i18n.language === 'tr' ? 'Bu hizmet bir seans paketi mi?' : 'Is this a session package?'}
                </Label>
                <p className="text-xs text-zinc-600 mt-1 font-medium">
                  {i18n.language === 'tr' ? 'Açarsanız seans sayısı girmeniz gerekir' : 'If enabled, you need to enter the number of sessions'}
                </p>
              </div>
              <Switch
                id="session-toggle"
                checked={formData.isSessionPackage}
                onCheckedChange={(checked) => setFormData({ ...formData, isSessionPackage: checked })}
              />
            </div>
            {formData.isSessionPackage && (
              <div className="space-y-2 animate-in fade-in duration-200">
                <Label htmlFor="session-count" className="text-sm font-bold text-zinc-700">
                  {i18n.language === 'tr' ? 'Kaç Seans?' : 'How many sessions?'}
                </Label>
                <Input
                  id="session-count"
                  type="number"
                  min="2"
                  max="50"
                  value={formData.sessionCount}
                  onChange={(e) => setFormData({ ...formData, sessionCount: e.target.value })}
                  placeholder="7"
                  required
                  className="backdrop-blur-md bg-white/60 border border-white/40 rounded-xl h-11 focus:ring-2 focus:ring-zinc-900 font-medium"
                />
                <p className="text-xs text-zinc-500 font-medium">
                  {i18n.language === 'tr' ? 'Minimum 2 seans gereklidir' : 'Minimum 2 sessions required'}
                </p>
              </div>
            )}

            {/* Section: Ödeme */}
            <div className="flex items-center gap-3 pt-2">
              <h3 className="text-[11px] font-black uppercase tracking-[0.12em] text-zinc-400">
                {i18n.language === 'tr' ? 'Ödeme' : 'Payment'}
              </h3>
              <div className="flex-1 h-px bg-zinc-200/70" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold text-zinc-700">
                {i18n.language === 'tr' ? 'Ödeme Kuralı' : 'Payment Rule'}
              </Label>
              {/* Seçim artık geri alınmıyor; fiyat düşükse uyarı seçimin hemen altında çıkıyor. */}
              <select
                value={formData.paymentRule}
                onChange={(e) => {
                  const rule = e.target.value;
                  const price = parseFloat(formData.price) || 0;
                  setFormData({ ...formData, paymentRule: rule });
                  if ((rule === 'online' || rule === 'deposit') && price > 0 && price < paymentMinimums.online) {
                    setFormErrors(prev => ({
                      ...prev,
                      paymentRule: rule === 'online'
                        ? (i18n.language === 'tr' ? `Online ödeme için hizmet fiyatı en az ${paymentMinimums.online}₺ olmalıdır.` : `Online payment requires a service price of at least £${paymentMinimums.online}.`)
                        : (i18n.language === 'tr' ? `Kapora için hizmet fiyatı en az ${paymentMinimums.online}₺ olmalıdır.` : `Taking a deposit requires a service price of at least £${paymentMinimums.online}.`),
                    }));
                  } else {
                    setFormErrors(prev => { const { paymentRule: _, ...rest } = prev; return rest; });
                  }
                }}
                className={`w-full backdrop-blur-md bg-white/60 border rounded-xl h-11 px-3 text-sm font-medium text-zinc-900 focus:ring-2 focus:ring-zinc-900 focus:outline-none ${formErrors.paymentRule ? 'border-red-400' : 'border-white/40'}`}
              >
                <option value="on_site">{i18n.language === 'tr' ? 'Yerinde Ödeme' : 'Pay On Site'}</option>
                <option value="online">{i18n.language === 'tr' ? 'Tamamı Online Ödeme' : 'Full Online Payment'}</option>
                <option value="deposit">{i18n.language === 'tr' ? 'Sadece Kapora' : 'Deposit Only'}</option>
              </select>
              <p className="text-xs text-zinc-500 font-medium">
                {formData.paymentRule === 'online'
                  ? (i18n.language === 'tr' ? 'Müşteri randevu alırken ödemenin tamamını online yapar.' : 'Customer pays full amount online when booking.')
                  : formData.paymentRule === 'deposit'
                    ? (i18n.language === 'tr' ? 'Müşteri randevu alırken sabit kapora öder, kalanı işletmede.' : 'Customer pays fixed deposit online, rest at venue.')
                    : (i18n.language === 'tr' ? 'Müşteri ödemeyi işletmede yapar, online ödeme alınmaz.' : 'Customer pays at the venue, no online payment.')
                }
              </p>
              {formErrors.paymentRule
                ? <p className="text-xs text-red-500 font-medium">{formErrors.paymentRule}</p>
                : (formData.paymentRule === 'online' || formData.paymentRule === 'deposit') && (
                  <p className="text-xs text-zinc-400 font-medium">
                    {i18n.language === 'tr'
                      ? `Online ödeme ve kapora için minimum hizmet fiyatı ${paymentMinimums.online}₺`
                      : `Online payment and deposits require a minimum service price of £${paymentMinimums.online}.`}
                  </p>
                )
              }
            </div>

            {formData.paymentRule === 'deposit' && (
              <div className="space-y-2 animate-in fade-in duration-200">
                <Label htmlFor="deposit-amount" className="text-sm font-bold text-zinc-700">
                  {i18n.language === 'tr' ? 'Kapora Tutarı (₺)' : 'Deposit Amount (£)'}
                </Label>
                <Input
                  id="deposit-amount"
                  type="number"
                  min={paymentMinimums.deposit}
                  max={Math.round(parseFloat(formData.price) || 0) || 9999}
                  value={formData.depositAmount}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    const price = parseFloat(formData.price) || 0;
                    if (val > price && price > 0) {
                      setFormErrors(prev => ({ ...prev, depositAmount: i18n.language === 'tr' ? 'Kapora tutarı, hizmetin toplam fiyatını aşamaz.' : 'Deposit cannot exceed the service price.' }));
                      return;
                    }
                    setFormErrors(prev => { const { depositAmount: _, ...rest } = prev; return rest; });
                    setFormData({ ...formData, depositAmount: e.target.value });
                  }}
                  placeholder={i18n.language === 'tr' ? `Min ${paymentMinimums.deposit}₺` : `Min £${paymentMinimums.deposit}`}
                  required
                  className={`backdrop-blur-md bg-white/60 border rounded-xl h-11 focus:ring-2 focus:ring-zinc-900 font-medium ${formErrors.depositAmount ? 'border-red-400' : 'border-white/40'}`}
                />
                {formErrors.depositAmount
                  ? <p className="text-xs text-red-500 font-medium">{formErrors.depositAmount}</p>
                  : <p className="text-xs text-zinc-500 font-medium">
                      {i18n.language === 'tr' 
                        ? `Min ${paymentMinimums.deposit}₺. Kalan tutar işletmede ödenir.`
                        : `Minimum £${paymentMinimums.deposit}. The rest is paid at the venue.`}
                    </p>
                }
              </div>
            )}

            <DialogFooter className="flex-shrink-0 border-t border-white/30 pt-4 bg-white/95 sticky bottom-0">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowDialog(false)}
                className="backdrop-blur-md bg-white/60 border-white/40 hover:bg-white/80 rounded-xl h-11 font-bold"
              >
                {t('common.cancel')}
              </Button>
              <Button
                data-testid="save-service-button"
                type="submit"
                disabled={loading}
                className="bg-zinc-900 hover:bg-black rounded-xl h-11 font-bold shadow-lg"
              >
                {loading ? t('settings.profile.buttons.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/*
        Hizmet ekledikten sonra 'önce personele atayın' hatırlatması.
        Backend artık yeni hizmeti tüm aktif admin+staff'a otomatik atadığı için dialog kullanılmıyor.
        Geri açmak istersen bu bloğu yorum dışına çıkar ve yukarıdaki setLastAddedServiceName/setShowAssignReminder çağrılarını da aç.

      <Dialog open={showAssignReminder} onOpenChange={setShowAssignReminder}>
        <DialogContent className="backdrop-blur-2xl bg-white/75 border border-white/40 rounded-3xl shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-zinc-900">
              {i18n.language === 'en' ? 'Reminder' : 'Hatırlatma'}
            </DialogTitle>
            <DialogDescription className="text-zinc-700 font-semibold">
              {i18n.language === 'en'
                ? `After adding${lastAddedServiceName ? ` “${lastAddedServiceName}”` : ''}, don't forget to assign it to your staff from Staff Management.`
                : `Hizmeti${lastAddedServiceName ? ` “${lastAddedServiceName}”` : ''} ekledikten sonra, lütfen Personel Yönetimi sayfasından personelinize tanımlamayı unutmayın.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="border-t border-white/30 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAssignReminder(false)}
              className="backdrop-blur-md bg-white/60 border-white/40 hover:bg-white/80 rounded-xl h-11 font-bold"
            >
              {i18n.language === 'en' ? 'Close' : 'Kapat'}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowAssignReminder(false);
                onNavigate && onNavigate("staff");
              }}
              className="bg-zinc-900 hover:bg-black rounded-xl h-11 font-bold shadow-lg"
            >
              {i18n.language === 'en' ? 'Go to Staff Management' : 'Personel Yönetimi’ne Git'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent className="backdrop-blur-2xl bg-white/95 border-white/30 rounded-3xl shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-black text-zinc-900">{t('services.management.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600 font-medium">
              {t('services.management.deleteDescription', { name: deleteDialog?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="backdrop-blur-md bg-white/60 border-white/40 hover:bg-white/80 rounded-xl font-bold">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-service"
              onClick={() => handleDelete(deleteDialog?.id)}
              className="bg-red-500 hover:bg-red-600 rounded-xl font-bold shadow-lg"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
        <DialogContent className="max-w-md backdrop-blur-2xl bg-white/95 border-white/30 rounded-3xl shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-zinc-900">
              {editingCategory ? t('services.categories.editTitle', 'Kategori Düzenle') : t('services.categories.addTitle', 'Kategori Ekle')}
            </DialogTitle>
            <DialogDescription className="text-zinc-600 font-medium">
              {t('services.categories.dialogDescription', 'Hizmetlerinizi gruplamak için bir kategori adı girin')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category-name" className="text-sm font-bold text-zinc-700">{t('services.categories.label', 'Kategori')}</Label>
              <Input
                id="category-name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder={t('services.categories.placeholder', 'Örn: Saç Hizmetleri')}
                required
                className="backdrop-blur-md bg-white/60 border border-white/40 rounded-xl h-11 focus:ring-2 focus:ring-zinc-900 font-medium"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCategoryDialog(false)} className="backdrop-blur-md bg-white/60 border-white/40 hover:bg-white/80 rounded-xl h-11 font-bold">
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={savingCategory} className="bg-zinc-900 hover:bg-black rounded-xl h-11 font-bold shadow-lg">
                {savingCategory ? t('settings.profile.buttons.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Kategoriye toplu hizmet atama */}
      <Dialog open={!!assignCategory} onOpenChange={(open) => { if (!open && !savingAssignment) setAssignCategory(null); }}>
        <DialogContent
          className="max-w-md max-h-[78dvh] flex flex-col backdrop-blur-2xl bg-white/95 border-white/30 rounded-3xl shadow-2xl"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-xl font-black text-zinc-900">
              {t('services.categories.assignTitle')}
            </DialogTitle>
            <DialogDescription className="text-zinc-600 font-medium">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-zinc-900 mb-1.5">
                <Tag className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                <span className="break-words">{assignCategory?.name}</span>
              </span>
              <span className="block">{t('services.categories.assignDescription')}</span>
            </DialogDescription>
          </DialogHeader>

          {(services || []).length > 8 && (
            <div className="relative shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                value={assignSearch}
                onChange={(e) => setAssignSearch(e.target.value)}
                placeholder={t('services.categories.assignSearchPlaceholder')}
                className="pl-9 backdrop-blur-md bg-white/60 border border-white/40 rounded-xl h-11 focus:ring-2 focus:ring-zinc-900 font-medium"
              />
            </div>
          )}

          <div className="flex-1 overflow-y-auto overflow-x-hidden space-y-2 py-1 pr-1">
            {assignableServices.length === 0 ? (
              <p className="text-sm text-zinc-500 font-medium text-center py-8">
                {(services || []).length === 0
                  ? t('services.categories.assignNoServices')
                  : t('services.categories.assignNoResults')}
              </p>
            ) : (
              assignableServices.map((service) => {
                const selected = assignSelection.has(service.id);
                const otherCategory = service.category_id && service.category_id !== assignCategory?.id
                  ? categoryNameById[service.category_id]
                  : null;
                return (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => toggleAssignService(service.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
                      selected
                        ? 'bg-zinc-900 border-zinc-900 text-white'
                        : 'backdrop-blur-md bg-white/50 border-white/40 hover:bg-white/80 text-zinc-900'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                      selected ? 'bg-white border-white' : 'border-zinc-300'
                    }`}>
                      {selected && <Check className="w-3.5 h-3.5 text-zinc-900" strokeWidth={3} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-bold break-words leading-snug">{service.name}</span>
                      {otherCategory && !selected && (
                        <span className="block text-xs text-zinc-500 font-medium mt-0.5">
                          {t('services.categories.currentlyIn', { category: otherCategory })}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <DialogFooter className="shrink-0 border-t border-zinc-200 pt-4">
            <Button
              type="button"
              variant="outline"
              disabled={savingAssignment}
              onClick={() => setAssignCategory(null)}
              className="backdrop-blur-md bg-white/60 border-white/40 hover:bg-white/80 rounded-xl h-11 font-bold"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleSaveAssignment}
              disabled={savingAssignment}
              className="bg-zinc-900 hover:bg-black rounded-xl h-11 font-bold shadow-lg"
            >
              {savingAssignment
                ? t('settings.profile.buttons.saving')
                : t('services.categories.assignSaveButton', { count: assignSelection.size })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Delete Confirmation */}
      <AlertDialog open={!!deleteCategoryDialog} onOpenChange={() => setDeleteCategoryDialog(null)}>
        <AlertDialogContent className="backdrop-blur-2xl bg-white/95 border-white/30 rounded-3xl shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-black text-zinc-900">{t('services.categories.deleteTitle', 'Kategoriyi Sil')}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600 font-medium">
              {t('services.categories.deleteDescription', 'Bu kategori silinecek. Bağlı hizmetler kategorisiz kalacak (silinmez).')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="backdrop-blur-md bg-white/60 border-white/40 hover:bg-white/80 rounded-xl font-bold">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleCategoryDelete(deleteCategoryDialog?.id)}
              className="bg-red-500 hover:bg-red-600 rounded-xl font-bold shadow-lg"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ServiceManagement;